<script setup>
import { ref, computed, watch, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useMainStore } from "@/stores/store.js";
import RoomCanvas from "./RoomCanvas.vue";
import StepReport_ProductCard from "./StepReport_ProductCard.vue";
import StepReport_FloatingWidget from "./StepReport_FloatingWidget.vue";
import StepReport_ResultsWidget from "./StepReport_ResultsWidget.vue";
import StepReport_AddProductModal from "./StepReport_Modal_AddProduct.vue";
import StepReport_EditRoomModal from "./StepReport_Modal_EditRoom.vue";
import BaseConfirmDialog from "@/components/utils/BaseConfirmDialog.vue";
import { Utils } from "@/utils.js";
import {
	PhPencil,
	PhPlus,
	PhArrowUUpLeft,
	PhWarningCircle,
} from "@phosphor-icons/vue";
import ModalError from "./StepReport_ModalError.vue";

const store = useMainStore();
const router = useRouter();

//
// Store cleanup function for focus trap to prevent memory leaks.
//
let cleanupFocusTrap = null;

const showSizeGuide = ref(false);
const showFloatingWidget = ref(true);
const showAddProductModal = ref(false);
const showEditRoomModal = ref(false);
const showResetConfirmation = ref(false);
const roomCanvasRef = ref(null);

//
// Check if products are too big for room.
//
const productTooBigForRoom = computed(() => {
	return roomCanvasRef.value?.productTooBigForRoom || false;
});

const roomAlreadyMeetsTarget = computed(() => {
	const baselineT60 = store.roomBaseline?.roomT60;
	const targetT60 = store.user.room.usagetarget;
	if (baselineT60 === undefined || !targetT60) return false;
	return Number(baselineT60) <= Number(targetT60);
});

/*
------------------------------------------------------------------------
Handle events
------------------------------------------------------------------------
*/
const handleStartAgainClick = () => {
	$log.log(`[StepReport_v2:handleStartAgainClick] Clicked`);
	if (import.meta.env.MODE !== "production") {
		store.user.reportGenerated = false;
	} else {
		store.resetUserData(false);
		store.gotoStep("Start", router, "previous");
	}
};

const handleAddProductClick = () => {
	showAddProductModal.value = true;
	document.body.classList.add("no-scroll");
};

const handleCloseAddProductModal = () => {
	showAddProductModal.value = false;
	document.body.classList.remove("no-scroll");
};

const handleEditRoomClick = () => {
	showEditRoomModal.value = true;
};

const handleCloseEditRoomModal = () => {
	showEditRoomModal.value = false;
};

const handleCloseFloatingWidget = () => {
	showFloatingWidget.value = false;
};

const handleResetProductsClick = () => {
	showResetConfirmation.value = true;
};

const handleConfirmResetProducts = async () => {
	await store.resetProducts();
	showResetConfirmation.value = false;
};

const handleCancelResetProducts = () => {
	showResetConfirmation.value = false;
};

/*
------------------------------------------------------------------------
Modal handling
------------------------------------------------------------------------
*/
//
// Focus first element in modal
//
const modal_focusFirstElement = () => {
	nextTick(() => {
		const modal = document.querySelector(".lux-modal");
		if (modal) {
			const firstFocusableElement = modal.querySelector(
				'input, [tabindex]:not([tabindex="-1"])',
			);
			if (firstFocusableElement) {
				firstFocusableElement.focus();
			}
		}
	});
};

//
// Cancel error overlay
//
const cancel_modal_ErrorOverlay = () => {
	$log.log(`[StepReport_v2:cancel_modal_ErrorOverlay] Closing error overlay`);
	store.overlays.reporterror.open = false;
	store.overlays.reporterror.message = "";
	document.body.classList.remove("no-scroll");
	// Clean up focus trap
	if (cleanupFocusTrap) {
		cleanupFocusTrap();
		cleanupFocusTrap = null;
	}
};

//
// Open error overlay
//
const open_modal_ErrorOverlay = () => {
	$log.log(`[StepReport_v2:open_modal_ErrorOverlay] Opening error overlay`);
	store.overlays.reporterror.open = true;
	document.body.classList.add("no-scroll");
	nextTick(() => {
		modal_focusFirstElement();
		cleanupFocusTrap = Utils.trapModalFocus(".lux-modal");
	});
};

//
// Watch for error overlay open state
//
watch(
	() => [store.overlays.reporterror.open],
	(newValue, oldValue) => {
		if (newValue[0] === true) {
			open_modal_ErrorOverlay();
		} else {
			cancel_modal_ErrorOverlay();
		}
	},
);
</script>

<template>
	<div id="step-report-v2" class="lux-step-root">
		<!--
        SUCCESS SCREEN
        -->

		<div
			class="lux-mt w-full border-b text-black"
			v-if="store.user.reportGenerated"
		>
			<div
				class="w-full bg-white px-4 py-4 sm:px-10 sm:py-12 md:px-20 md:py-12"
			>
				<h2 class="lux-page-title">Your report has been sent!</h2>
				<p class="lux-copy-xlarge">
					Your customised acoustic report has been generated and emailed to
					{{ store.user.email }}. Thank you for using the Luxxbox Acoustic
					Analyzer.
				</p>

				<div class="mt-10 flex gap-8">
					<button
						type="button"
						class="lux-btn lux-btn-black"
						@click="handleStartAgainClick"
					>
						Start again
					</button>
				</div>
			</div>
		</div>

		<!--
        REPORT SUMMARY
        -->

		<div
			class="bg-white text-black"
			v-if="!store.user.reportGenerated && store.hasProducts"
		>
			<!-- Header Section -->
			<div
				class="w-full border-b border-gray-200 bg-gray-100 px-4 py-4 sm:py-8 md:px-8 md:py-10 lg:px-8 2xl:px-10"
			>
				<h2
					class="mb-1 text-sm uppercase text-gray-800 sm:mb-2 sm:text-base md:mb-3 md:text-lg"
				>
					Report Summary
				</h2>
				<p
					class="text-xl font-bold text-gray-800 sm:text-2xl md:text-3xl lg:text-5xl"
				>
					{{ store.user.projectname }} / {{ store.user.roomname }}
				</p>
			</div>

			<div class="grid grid-cols-12">
				<div
					class="col-span-12 bg-white p-4 pb-96 md:pb-64 md:col-span-8 md:p-8 lg:px-8 xl:col-span-9 2xl:px-10"
				>
					<!--
					Room Already Meets Target Alert
					-->
					<div
						v-if="roomAlreadyMeetsTarget"
						class="mb-6 flex items-start gap-3 bg-black p-4 text-white"
					>
						<PhWarningCircle
							:size="24"
							weight="fill"
							class="mt-0.5 flex-shrink-0"
						/>
						<p class="text-sm md:text-lg leading-relaxed font-bold">
							Based on your specification, this room already meets the desired
							reverberation time with a starting T60 of
							{{ store.startingT60 }}s. However, more acoustic surface area
							provides extra absorption which helps manage higher noise levels.
							Consider adding acoustic lighting to meet your lighting needs
							while further improving room acoustics. We've included 1x
							{{ store.currentProduct?.data?.name }} to address your lighting
							requirements — the added absorption is a bonus.
						</p>
					</div>

					<!--
					Room Characteristics Section
					-->
					<section class="lx-report-section">
						<div class="lx-report-section-header">
							<h3 class="lux-copy-large lux-copy-semibold uppercase">
								Room Characteristics
							</h3>
							<!-- Edit Room Details Button -->
							<button
								type="button"
								class="lux-btn lux-btn-small lux-btn-black flex items-center gap-2 whitespace-nowrap"
								@click="handleEditRoomClick"
							>
								<PhPencil :size="16" />Edit Room Details
							</button>
						</div>

						<div
							class="max1-h-96 overflow-y-auto border-l border-t border-gray-200 md:max-h-none"
						>
							<table class="reportTable lux-copy-normal w-full">
								<tbody class="align-middle">
									<tr>
										<th>Intended Use</th>
										<td>
											{{ store.user.room.usage.replace(/[_]/g, " ") }}
										</td>
									</tr>
									<tr>
										<th>Materials</th>
										<td>
											<span
												v-for="(item, index) in store.user.room.materials"
												:key="index"
											>
												<span class="capitalize">{{
													item.replace(/_/g, " ")
												}}</span>
												<span
													v-if="index < store.user.room.materials.length - 1"
													>,&nbsp;</span
												>
											</span>
											<span v-if="store.user.room.materials.length === 0"
												><em>None selected.</em></span
											>
										</td>
									</tr>
									<tr>
										<th>Unit</th>
										<td>
											<span class="capitalize">{{ store.user.room.unit }}</span>
											({{ store.unitCodeLong(store.user.room.unit) }})
										</td>
									</tr>
									<tr>
										<th>Width</th>
										<td v-if="store.user.room.unit === 'imperial'">
											{{ store.calcWidth("imperial") }}
											<span class="ml-3 text-black/25">{{
												store.calcWidth("metric")
											}}</span>
										</td>
										<td v-if="store.user.room.unit === 'metric'">
											{{ store.calcWidth("metric") }}
											<span class="ml-3 text-black/25">{{
												store.calcWidth("imperial")
											}}</span>
										</td>
									</tr>
									<tr>
										<th>Length</th>
										<td v-if="store.user.room.unit === 'imperial'">
											{{ store.calcLength("imperial") }}
											<span class="ml-3 text-black/25">{{
												store.calcLength("metric")
											}}</span>
										</td>
										<td v-if="store.user.room.unit === 'metric'">
											{{ store.calcLength("metric") }}
											<span class="ml-3 text-black/25">{{
												store.calcLength("imperial")
											}}</span>
										</td>
									</tr>
									<tr>
										<th>Ceiling Height</th>
										<td v-if="store.user.room.unit === 'imperial'">
											{{ store.calcHeight("imperial") }}
											<span class="ml-3 text-black/25">{{
												store.calcHeight("metric")
											}}</span>
										</td>
										<td v-if="store.user.room.unit === 'metric'">
											{{ store.calcHeight("metric") }}
											<span class="ml-3 text-black/25">{{
												store.calcHeight("imperial")
											}}</span>
										</td>
									</tr>
									<tr>
										<th>Floor Area</th>
										<td v-if="store.user.room.unit === 'imperial'">
											{{ store.calcFloorArea("imperial") }}
											<span class="ml-3 text-black/25">{{
												store.calcFloorArea("metric")
											}}</span>
										</td>
										<td v-if="store.user.room.unit === 'metric'">
											{{ store.calcFloorArea("metric") }}
											<span class="ml-3 text-black/25">{{
												store.calcFloorArea("imperial")
											}}</span>
										</td>
									</tr>
									<tr>
										<th>Ceiling Area</th>
										<td v-if="store.user.room.unit === 'imperial'">
											{{ store.calcFloorArea("imperial") }}
											<span class="ml-3 text-black/25">{{
												store.calcFloorArea("metric")
											}}</span>
										</td>
										<td v-if="store.user.room.unit === 'metric'">
											{{ store.calcFloorArea("metric") }}
											<span class="ml-3 text-black/25">{{
												store.calcFloorArea("imperial")
											}}</span>
										</td>
									</tr>
									<tr>
										<th>Wall Area</th>
										<td v-if="store.user.room.unit === 'imperial'">
											{{ store.calcWallArea("imperial") }}
											<span class="ml-3 text-black/25">{{
												store.calcWallArea("metric")
											}}</span>
										</td>
										<td v-if="store.user.room.unit === 'metric'">
											{{ store.calcWallArea("metric") }}
											<span class="ml-3 text-black/25">{{
												store.calcWallArea("imperial")
											}}</span>
										</td>
									</tr>
									<tr>
										<th>Total Surface Area</th>
										<td v-if="store.user.room.unit === 'imperial'">
											{{ store.calcSurfaceArea("imperial") }}
											<span class="ml-3 text-black/25">{{
												store.calcSurfaceArea("metric")
											}}</span>
										</td>
										<td v-if="store.user.room.unit === 'metric'">
											{{ store.calcSurfaceArea("metric") }}
											<span class="ml-3 text-black/25">{{
												store.calcSurfaceArea("imperial")
											}}</span>
										</td>
									</tr>
									<tr>
										<th>Volume</th>
										<td v-if="store.user.room.unit === 'imperial'">
											{{ store.calcVolume("imperial") }}
											<span class="ml-3 text-black/25">{{
												store.calcVolume("metric")
											}}</span>
										</td>
										<td v-if="store.user.room.unit === 'metric'">
											{{ store.calcVolume("metric") }}
											<span class="ml-3 text-black/25">{{
												store.calcVolume("imperial")
											}}</span>
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</section>

					<!--
					Acoustic Characteristics Section
					-->
					<section class="lx-report-section">
						<div class="lx-report-section-header">
							<h3 class="lux-copy-large lux-copy-semibold uppercase">
								Acoustic Characteristics
							</h3>
						</div>

						<div
							class="max1-h-96 overflow-y-auto border-l border-t border-gray-200 md:max-h-none"
						>
							<table class="reportTable lux-copy-normal w-full">
								<tbody class="align-middle">
									<tr>
										<th>Starting T60</th>
										<td>{{ store.roomBaseline?.roomT60 || "—" }}s</td>
									</tr>
									<tr>
										<th>Target T60</th>
										<td>{{ store.user.room.usagetarget || "—" }}s</td>
									</tr>
									<tr>
										<th>Starting Sabins</th>
										<td>{{ store.roomBaseline?.roomMetricSabins || "—" }}</td>
									</tr>
									<tr>
										<th>Target Sabins</th>
										<td>
											{{
												store.combinedCalculation?.requiredTotalMetricSabins ||
												"—"
											}}
										</td>
									</tr>
								</tbody>
							</table>
						</div>
					</section>

					<!--
					Product Characteristics Section
					-->
					<section class="lx-report-section">
						<div class="lx-report-section-header">
							<h3 class="lux-copy-large lux-copy-semibold uppercase">
								Product Characteristics
							</h3>
						</div>

						<div
							class="flex flex-col lg:flex-row items-start md:gap-4 mb-4 lg:mb-0"
						>
							<p class="mb-3 text-sm text-gray-700 md:text-base">
								Add other acoustic lighting or baffling products to your report
								to experiment with different combinations. Adjust the product
								properties to find the perfect configuration for your space.
							</p>
							<button
								type="button"
								class="lux-btn lux-btn-small lux-btn-black flex items-center gap-2 whitespace-nowrap mt-1 md:mt-0"
								@click="handleAddProductClick"
							>
								<PhPlus :size="16" class="w-4 h-4" />Add a Product
							</button>
						</div>

						<!--
						Product Cards
						-->
						<div class="space-y-4">
							<StepReport_ProductCard
								v-for="(product, index) in store.user.products"
								:key="product.id"
								:product="product"
								:product-index="index"
								:is-primary="index === 0"
							/>
						</div>

						<!--
						Add Product and Reset Products Button
						-->
						<div
							class="mt-4 flex flex-col lg:flex-row justify-center gap-4 text-center"
						>
							<button
								v-if="store.hasCustomProductSelections"
								type="button"
								id="reset-products-button"
								class="lux-btn lux-btn-small lux-btn-outline flex flex-row items-center gap-2 justify-center order-2 lg:order-1"
								@click="handleResetProductsClick"
							>
								<PhArrowUUpLeft :size="16" /> Reset Products
							</button>
							<button
								type="button"
								id="add-product-button"
								class="lux-btn lux-btn-small lux-btn-black flex items-center gap-2 justify-center order-1 lg:order-2"
								@click="handleAddProductClick"
								v-if="store.user.products.length > 1"
							>
								<PhPlus :size="16" />Add another Product
							</button>
							<button
								type="button"
								id="add-product-button"
								class="lux-btn lux-btn-small lux-btn-black flex items-center gap-2 justify-center order-1 lg:order-2"
								@click="handleAddProductClick"
								v-if="store.user.products.length === 1"
							>
								<PhPlus :size="16" />Add a Product
							</button>
						</div>
					</section>

					<!--
					Room/Product Mockup
					-->
					<section class="lx-report-section">
						<!--
						Warning when products are too big
						-->
						<!-- <div v-if="productTooBigForRoom" class="mb-4 border-2 border-red-600 bg-red-50 p-4 text-red-900">
                            <p class="font-semibold">⚠ Warning: Product is too large for this room</p>
                            <p class="mt-1 text-sm">The selected product exceeds the room dimensions and cannot be properly installed in this space. Please select a smaller product or increase the room size.</p>
                        </div> -->

						<RoomCanvas ref="roomCanvasRef" />
					</section>
				</div>
				<!-- <div class="results-container sticky bottom-[60px] z-600 col-span-12 self-end p-3 md:bottom-auto md:top-10 md:col-span-4 md:self-auto xl:col-span-3"> -->
				<div
					class="results-container sticky bottom-[60px] z-600 col-span-12 self-end md:bottom-auto md:top-10 md:col-span-4 md:self-auto md:p-4 lg:p-8 md:pt-8 xl:col-span-3 pl-0 md:pl-0 lg:pl-0"
				>
					<StepReport_ResultsWidget
						:product-too-big="productTooBigForRoom"
						@close="handleCloseFloatingWidget"
						@add-product="handleAddProductClick"
					/>
				</div>
			</div>
			<!-- <div class="bg-gray-100">
				room mockup
				<br /><br /><br /><br /><br /><br /><br /><br /><br /><br />
				<br /><br /><br /><br /><br /><br /><br /><br /><br /><br />
				<br /><br /><br /><br /><br /><br /><br /><br /><br /><br />
				<br /><br /><br /><br /><br /><br /><br /><br /><br /><br />
				<br /><br /><br /><br /><br /><br /><br /><br /><br /><br />
			</div> -->
		</div>

		<span class="hidden">
			<input
				type="hidden"
				name="stepReportValid"
				:value="store.stepReportIsValid"
				class="hidden"
			/>
		</span>

		<!--
        ADD PRODUCT MODAL
        Component stays mounted, visibility controlled by :show prop for smooth transitions
        -->
		<Teleport to="body">
			<StepReport_AddProductModal
				:show="showAddProductModal"
				:fnCancelModal="handleCloseAddProductModal"
			/>
		</Teleport>

		<!--
        EDIT ROOM MODAL
        Component stays mounted, visibility controlled by :show prop for smooth transitions
        -->
		<Teleport to="body">
			<StepReport_EditRoomModal
				:show="showEditRoomModal"
				:fnCancelModal="handleCloseEditRoomModal"
			/>
		</Teleport>

		<!--
        REPORT ERROR MODAL OVERLAY
        -->
		<transition name="stepfade">
			<Teleport to="body">
				<ModalError
					:fnCancelModal="cancel_modal_ErrorOverlay"
					:fnFocusFirstElement="modal_focusFirstElement"
					v-if="store.overlays.reporterror.open"
				/>
			</Teleport>
		</transition>

		<!-- Reset Products Confirmation Dialog -->
		<BaseConfirmDialog
			:show="showResetConfirmation"
			title="Reset Products"
			message="Are you sure you want to reset all products back to your original selection?"
			confirmLabel="Reset"
			@confirm="handleConfirmResetProducts"
			@cancel="handleCancelResetProducts"
		/>
	</div>
</template>

<style>
.lx-report-section {
	@apply my-0 mb-6 md:mb-12;
}

.lx-report-section-header {
	@apply flex flex-col items-start justify-between gap-2 pb-4 md:flex-row md:gap-4 md:pb-4;
}

.results-container {
	position: sticky;
	top: 116px;
	align-self: start;
}

.reportTable {
	@apply w-full border-gray-200;
}

.reportTable tbody tr {
}

.reportTable tbody td,
.reportTable tbody th {
	@apply p-2 px-3 text-left text-xs font-medium text-black sm:p-3 md:text-sm;
}

.reportTable tbody th {
	@apply w-2/5 border-b border-r border-gray-200 bg-gray-100 font-bold capitalize;
}

.reportTable tbody td {
	@apply border-b border-r border-gray-200;
}
</style>
